import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Spin,
  Form,
  Input,
  Select,
  InputNumber,
  Button,
  Modal,
  message,
  DatePicker,
} from "antd";
import { DeleteOutlined, PrinterOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { BulkBarcodePrintModal } from "../../products/common";
import { useQueryClient } from "@tanstack/react-query";
import {
  useProductsQuery,
  usePurchaseByIdQuery,
  useCreatePurchaseMutation,
  useUpdatePurchaseMutation,
  useScanInvoiceMutation,
} from "../../../Services";
import {
  Purchase,
  PurchaseItem,
  Supplier,
  Product,
  Stock,
  PurchaseStatus,
  PurchasePaymentStatus,
} from "../../../Types";
import { PurchaseForm } from "./PurchaseForm";
import { PurchaseView } from "./PurchaseView";
import { SupplierModal } from "../../../components";
import { drawerFooter, drawerTitle } from "../../../components";

const { Option } = Select;

interface EditablePurchaseItem extends PurchaseItem {
  stocks?: Stock[];
}

interface PurchaseFormProps {
  open: boolean;
  onClose: (refetch?: boolean) => void;
  editingPurchase: Purchase | null;
  suppliers: Supplier[];
  refetchSuppliers: () => void;
}

export const PurchaseDrawer: React.FC<PurchaseFormProps> = ({
  open,
  onClose,
  editingPurchase,
  suppliers,
  refetchSuppliers,
}) => {
  const [form] = Form.useForm();
  const [purchaseItems, setPurchaseItems] = useState<EditablePurchaseItem[]>(
    [],
  );
  const [globalDiscount, setGlobalDiscount] = useState<number>(0);
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [paymentStatusState, setPaymentStatusState] =
    useState<PurchasePaymentStatus>(PurchasePaymentStatus.DUE);
  const [showDueDate, setShowDueDate] = useState<boolean>(true);
  const [productSearchText, setProductSearchText] = useState("");
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [scanning, setScanning] = useState(false);
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [grossTotal, setGrossTotal] = useState<number>(0);
  const [grandTotal, setGrandTotal] = useState<number>(0);
  const [dueAmount, setDueAmount] = useState<number>(0);
  const [bulkPrintOpen, setBulkPrintOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getBulkPrintItems = (): any[] => {
    const sourceItems = (editingPurchase && (editingPurchase.status === PurchaseStatus.COMPLETED || editingPurchase.status === PurchaseStatus.RETURNED) && fullPurchase)
      ? fullPurchase.items || []
      : purchaseItems;

    return sourceItems.map((item: any) => ({
      title: item.product_title || "Unknown Product",
      sku_code: item.product_sku || "",
      barcode: item.product_barcode || item.product_id?.toString() || "12345678",
      mrp: item.retail_price || 0,
      retail_price: item.retail_price || 0,
      cost_price: item.cost_price || 0,
      quantity: item.quantity || 1
    }));
  };

  const { data: productsList } = useProductsQuery(
    { page: 1, limit: 1000 },
    { enabled: open },
  );

  const queryClient = useQueryClient();
  const {
    data: fullPurchase,
    isFetching: loadingDetails,
    refetch: refetchPurchase,
  } = usePurchaseByIdQuery(editingPurchase?.id || 0, {
    enabled: open && !!editingPurchase,
  });

  const handlePaymentChange = () => {
    refetchPurchase();
    queryClient.invalidateQueries({ queryKey: ["purchasePayments"] });
    queryClient.invalidateQueries({ queryKey: ["purchases"] });
  };

  const scanInvoiceMutation = useScanInvoiceMutation();
  const createPurchaseMutation = useCreatePurchaseMutation();
  const updatePurchaseMutation = useUpdatePurchaseMutation();

  useEffect(() => {
    if (productsList) {
      setAllProducts(productsList?.data || []);
    }
  }, [productsList]);

  useEffect(() => {
    if (!open) return;

    if (editingPurchase && fullPurchase) {
      form.resetFields();
      form.setFieldsValue({
        supplier_id: fullPurchase.supplier_id,
        purchase_date: dayjs(fullPurchase.purchase_date),
        invoice_no: fullPurchase.invoice_no,
        po_no: fullPurchase.po_no,
        due_date: fullPurchase.due_date ? dayjs(fullPurchase.due_date) : null,
        payment_status: fullPurchase.payment_status,
      });

      setGlobalDiscount(fullPurchase.discount_amount);
      setPaidAmount(fullPurchase.paid_amount);
      setPaymentStatusState(fullPurchase.payment_status);

      const mappedItems: EditablePurchaseItem[] = (
        fullPurchase.items || []
      ).map((item: any) => {
        const activeProd = allProducts.find((p) => p.id === item.product_id);
        return {
          product_id: item.product_id,
          product_title: item.product_title,
          product_sku: item.product_sku,
          product_unit: item.product_unit,
          product_barcode: item.product_barcode,
          stock_id: item.stock_id,
          cost_price: item.cost_price,
          retail_price: item.retail_price,
          wholesale_price: item.wholesale_price,
          quantity: item.quantity,
          profit_margin: item.profit_margin,
          discount_percent: item.discount_percent,
          total: item.total,
          batch_no: item.batch_no || item.stock_batch_no,
          stock_option:
            item.stock_option || (item.stock_id ? "existing" : "new"),
          stocks: activeProd ? activeProd.stocks : [],
          expiry_date: item.expiry_date || null,
        };
      });

      setPurchaseItems(mappedItems);
    }
  }, [open, editingPurchase, fullPurchase, allProducts]);

  useEffect(() => {
    if (open && !editingPurchase) {
      form.resetFields();
      setPurchaseItems([]);
      setGlobalDiscount(0);
      setPaidAmount(0);
      setPaymentStatusState(PurchasePaymentStatus.DUE);

      form.setFieldsValue({
        purchase_date: dayjs(),
        payment_status: PurchasePaymentStatus.DUE,
      });
    }
  }, [open, editingPurchase]);

  useEffect(() => {
    const gross = purchaseItems.reduce(
      (sum, item) => sum + (item.total || 0),
      0,
    );
    const grand = Math.max(0, gross - globalDiscount);
    const due = Math.max(0, grand - paidAmount);

    setGrossTotal(gross);
    setGrandTotal(grand);
    setDueAmount(due);

    let pStatus = PurchasePaymentStatus.DUE;
    if (due <= 0) {
      pStatus = PurchasePaymentStatus.PAID;
      setShowDueDate(false);
    } else if (paidAmount > 0) {
      pStatus = PurchasePaymentStatus.PARTIAL;
      setShowDueDate(true);
    } else {
      pStatus = PurchasePaymentStatus.DUE;
      setShowDueDate(true);
    }
    setPaymentStatusState(pStatus);
    form.setFieldsValue({ payment_status: pStatus });
  }, [purchaseItems, globalDiscount, paidAmount, form]);

  const handleScanInvoice = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const mimeType = file.type;

    if (
      !["image/jpeg", "image/png", "image/webp", "application/pdf"].includes(
        mimeType,
      )
    ) {
      message.error(
        "Unsupported file type. Please upload a PDF or an Image (JPEG, PNG, WebP).",
      );
      return;
    }

    setScanning(true);
    const reader = new FileReader();

    reader.onload = async (evt) => {
      try {
        const base64Data = evt.target?.result as string;
        if (!base64Data) {
          message.error("Failed to read file.");
          setScanning(false);
          return;
        }

        const res = await scanInvoiceMutation.mutateAsync({
          base64Data,
          mimeType,
        });
        if (res.success && res.data) {
          const inv = res.data;

          form.setFieldsValue({
            invoice_no: inv.invoice_no || "",
            po_no: inv.po_no || "",
            purchase_date: inv.purchase_date && dayjs(inv.purchase_date).isValid()
              ? dayjs(inv.purchase_date)
              : dayjs(),
            supplier_id: inv.supplier_id || undefined,
          });

          if (inv.supplier_id) {
            message.success(`Matched supplier: ${inv.supplier_name}`);
          } else if (inv.supplier_name) {
            message.warning(
              `Could not find supplier matching: "${inv.supplier_name}". Please select or add supplier manually.`,
            );
          }

          const mappedItems: EditablePurchaseItem[] = (inv.items || [])
            .filter((item: any) => item !== null && item !== undefined)
            .map((item: any) => {
              return {
                product_id: item.product_id || 0,
                product_title: item.product_title || "Unknown Product",
                product_sku: item.product_sku || "",
                product_unit: item.product_unit || "Pcs",
                product_barcode: item.product_barcode || "",
                stock_option: item.stock_option || "new",
                stock_id: item.stock_id || null,
                batch_no: item.batch_no || "BATCH-new",
                cost_price: item.cost_price || 0,
                retail_price: item.retail_price || 0,
                wholesale_price: item.wholesale_price || 0,
                quantity: item.quantity || 1,
                profit_margin: item.profit_margin || 0,
                discount_percent: item.discount_percent || 0,
                total: item.total || 0,
                stocks: item.stocks || [],
                expiry_date: item.expiry_date || null,
              };
            });

          setPurchaseItems(mappedItems);
          message.success("Invoice successfully scanned and pre-filled!");
        } else {
          message.error(res.message || "Failed to parse invoice using AI.");
        }
      } catch (err: any) {
        console.error(err);
        message.error("An error occurred while scanning the document.");
      } finally {
        setScanning(false);
      }
    };

    reader.onerror = () => {
      message.error("Failed to read file.");
      setScanning(false);
    };

    try {
      reader.readAsDataURL(file);
    } catch (err) {
      console.error(err);
      message.error("Failed to start reading file.");
      setScanning(false);
    }
  };

  const triggerScanClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleSubmit = async (submitStatus: PurchaseStatus) => {
    try {
      const formValues = await form.validateFields();
      if (purchaseItems.length === 0) {
        message.error("Please add at least one item to purchase.");
        return;
      }

      const payload = {
        supplier_id: formValues.supplier_id,
        purchase_date: formValues.purchase_date.format("YYYY-MM-DD"),
        invoice_no: formValues.invoice_no || "",
        po_no: formValues.po_no || "",
        discount_amount: globalDiscount,
        paid_amount: paidAmount,
        payment_status: paymentStatusState,
        due_date: formValues.due_date
          ? formValues.due_date.format("YYYY-MM-DD")
          : null,
        status: submitStatus,
        items: purchaseItems.map((item) => ({
          product_id: item.product_id,
          product_title: item.product_title,
          stock_option: item.stock_option,
          stock_id: item.stock_id,
          cost_price: item.cost_price,
          retail_price: item.retail_price,
          wholesale_price: item.wholesale_price,
          quantity: item.quantity,
          profit_margin: item.profit_margin,
          discount_percent: item.discount_percent,
          total: item.total,
          batch_no: item.batch_no,
          expiry_date: item.expiry_date || null,
        })),
      };

      let res;
      if (editingPurchase) {
        res = await updatePurchaseMutation.mutateAsync({
          ...payload,
          id: editingPurchase.id,
        });
      } else {
        res = await createPurchaseMutation.mutateAsync(payload);
      }

      if (res?.success) {
        message.success(res.message || "Purchase saved successfully.");
        onClose(true);
      } else {
        message.error(res?.message || "Operation failed.");
      }
    } catch (err: any) {
      console.error(err);
      if (err?.errorFields) {
        return;
      }
      message.error(err?.message || "An unexpected error occurred.");
    }
  };

  const handleProductSelect = (value: string | number) => {
    if (value === "ADD_NEW_PRODUCT") {
      const newTitle = productSearchText.trim();
      if (!newTitle) return;

      if (
        purchaseItems.some(
          (item) =>
            item.product_title?.toLowerCase() === newTitle.toLowerCase(),
        )
      ) {
        message.warning(`"${newTitle}" is already added to the list.`);
        setProductSearchText("");
        return;
      }

      const initialItem: EditablePurchaseItem = {
        product_id: 0,
        product_title: newTitle,
        product_sku: "",
        product_unit: "Pcs",
        product_barcode: "",
        cost_price: 0,
        retail_price: 0,
        wholesale_price: 0,
        quantity: 1,
        profit_margin: 0,
        discount_percent: 0,
        total: 0,
        stock_option: "new",
        stock_id: null,
        batch_no: "BATCH-new",
        stocks: [],
        expiry_date: null,
      };

      setPurchaseItems([...purchaseItems, initialItem]);
      setProductSearchText("");
      return;
    }

    const productId = value;
    const product = allProducts.find((p) => p.id === productId);
    if (!product) return;

    if (purchaseItems.some((item) => item.product_id === productId)) {
      message.warning(`${product.title} is already added to the list.`);
      setProductSearchText("");
      return;
    }

    const defaultStock =
      product.stocks.find((s) => s.is_default === 1) || product.stocks[0];
    const initialItem: EditablePurchaseItem = {
      product_id: product.id,
      product_title: product.title,
      product_sku: product.sku_code,
      product_unit: product.unit,
      product_barcode: product.barcode,
      cost_price: defaultStock ? defaultStock.cost_price : 0,
      retail_price: defaultStock ? defaultStock.retail_price : 0,
      wholesale_price: defaultStock ? defaultStock.wholesale_price : 0,
      quantity: 1,
      profit_margin: defaultStock
        ? ((defaultStock.retail_price - defaultStock.cost_price) /
            defaultStock.cost_price) *
          100
        : 0,
      discount_percent: 0,
      total: defaultStock ? defaultStock.cost_price : 0,
      stock_option: defaultStock ? "existing" : "new",
      stock_id: defaultStock ? defaultStock.id : null,
      batch_no: defaultStock ? defaultStock.batch_no : "BATCH-new",
      stocks: product.stocks || [],
      expiry_date: defaultStock ? defaultStock.expiry_date : null,
    };

    setPurchaseItems([...purchaseItems, initialItem]);
    setProductSearchText("");
  };

  const updateItemField = (
    index: number,
    field: keyof EditablePurchaseItem,
    value: any,
  ) => {
    const updated = [...purchaseItems];
    const item = { ...updated[index] };

    (item as any)[field] = value;

    if (field === "stock_option") {
      if (value === "existing" && item.stocks && item.stocks.length > 0) {
        const defaultStock =
          item.stocks.find((s) => s.is_default === 1) || item.stocks[0];
        item.stock_id = defaultStock.id;
        item.batch_no = defaultStock.batch_no;
        item.cost_price = defaultStock.cost_price;
        item.retail_price = defaultStock.retail_price;
        item.wholesale_price = defaultStock.wholesale_price;
        item.expiry_date = defaultStock.expiry_date || null;
      } else {
        item.stock_id = null;
        item.batch_no = "BATCH-new";
        item.cost_price = 0;
        item.retail_price = 0;
        item.wholesale_price = 0;
        item.expiry_date = null;
      }
    }

    if (field === "stock_id") {
      const stock = item.stocks?.find((s) => s.id === value);
      if (stock) {
        item.batch_no = stock.batch_no;
        item.cost_price = stock.cost_price;
        item.retail_price = stock.retail_price;
        item.wholesale_price = stock.wholesale_price;
        item.expiry_date = stock.expiry_date || null;
      }
    }

    // Calculations
    const cost = item.cost_price || 0;
    const retail = item.retail_price || 0;

    if (field === "cost_price" || field === "retail_price") {
      item.profit_margin = cost > 0 ? ((retail - cost) / cost) * 100 : 0;
    }

    if (field === "profit_margin") {
      const margin = value || 0;
      item.retail_price = cost * (1 + margin / 100);
    }

    // Subtotal = cost_price * (1 - discount_percent/100) * quantity
    const qty = item.quantity || 0;
    const disc = item.discount_percent || 0;
    const netCost = item.cost_price * (1 - disc / 100);
    item.total = netCost * qty;

    updated[index] = item;
    setPurchaseItems(updated);
  };

  const removeItem = (index: number) => {
    setPurchaseItems(purchaseItems.filter((_, idx) => idx !== index));
  };

  const handleConfirmComplete = () => {
    Modal.confirm({
      title: "Complete Purchase Transaction?",
      content:
        "Completing this purchase will permanently lock it, register the products, and increase their stock batch levels. This action cannot be reversed.",
      okText: "Complete Purchase",
      okButtonProps: { type: "primary" },
      cancelText: "Cancel",
      onOk: () => handleSubmit(PurchaseStatus.COMPLETED),
    });
  };

  const closeForm = () => {
    onClose(false);
  };

  const formTableColumns = [
    {
      title: "Product",
      key: "product_details",
      width: "25%",
      render: (_: any, rec: EditablePurchaseItem) => (
        <div>
          <span className="font-semibold text-gray-800 block leading-tight">
            {rec.product_title}
          </span>
          <span className="text-[10px] text-gray-400 mt-1 block">
            SKU: {rec.product_sku || "New Product"}
          </span>
        </div>
      ),
    },
    {
      title: "Stock Mode / Batch",
      key: "stock_batch",
      width: 160,
      render: (_: any, rec: EditablePurchaseItem, index: number) => {
        const hasStocks = rec.stocks && rec.stocks.length > 0;
        return (
          <div className="flex flex-col gap-1.5">
            <Select
              size="small"
              value={rec.stock_option}
              onChange={(v) => updateItemField(index, "stock_option", v)}
              className="w-full text-[11px]"
            >
              <Option value="new">Add New Stock</Option>
              {hasStocks && (
                <Option value="existing">Select Existing Stock</Option>
              )}
            </Select>

            {rec.stock_option === "existing" ? (
              <Select
                size="small"
                value={rec.stock_id}
                onChange={(v) => updateItemField(index, "stock_id", v)}
                className="w-full text-[11px]"
              >
                {rec.stocks?.map((st) => (
                  <Option key={st.id} value={st.id}>
                    {st.batch_no}
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                size="small"
                placeholder="Batch No (Auto)"
                value={rec.batch_no === "BATCH-new" ? "" : rec.batch_no}
                onChange={(e) =>
                  updateItemField(index, "batch_no", e.target.value)
                }
                className="w-full text-[11px]"
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Expiry Date",
      key: "expiry_date",
      width: 140,
      render: (_: any, rec: EditablePurchaseItem, index: number) => {
        return (
          <DatePicker
            size="small"
            style={{ width: "100%" }}
            format="YYYY-MM-DD"
            placeholder="No Expiry"
            value={rec.expiry_date ? dayjs(rec.expiry_date) : null}
            onChange={(date) => {
              updateItemField(
                index,
                "expiry_date",
                date ? date.format("YYYY-MM-DD") : null,
              );
            }}
            disabled={rec.stock_option === "existing"}
          />
        );
      },
    },
    {
      title: "Qty",
      key: "quantity",
      width: 80,
      align: "center" as const,
      render: (_: any, rec: EditablePurchaseItem, index: number) => (
        <InputNumber
          size="small"
          min={1}
          value={rec.quantity}
          onChange={(v) => updateItemField(index, "quantity", v)}
          className="w-16"
        />
      ),
    },
    {
      title: "Cost",
      key: "cost_price",
      width: 96,
      render: (_: any, rec: EditablePurchaseItem, index: number) => (
        <InputNumber
          size="small"
          min={0}
          value={rec.cost_price}
          onChange={(v) => updateItemField(index, "cost_price", v)}
          formatter={(v) => `Rs. ${v}`}
          parser={(v) => (v ? v.replace(/Rs\.\s?|(,*)/g, "") : "") as any}
          className="w-24"
        />
      ),
    },
    {
      title: "Retail",
      key: "retail_price",
      width: 96,
      render: (_: any, rec: EditablePurchaseItem, index: number) => (
        <InputNumber
          size="small"
          min={0}
          value={rec.retail_price}
          onChange={(v) => updateItemField(index, "retail_price", v)}
          formatter={(v) => `Rs. ${v}`}
          parser={(v) => (v ? v.replace(/Rs\.\s?|(,*)/g, "") : "") as any}
          className="w-24"
        />
      ),
    },
    {
      title: "Margin (%)",
      key: "margin",
      width: 96,
      render: (_: any, rec: EditablePurchaseItem, index: number) => (
        <InputNumber
          size="small"
          value={rec.profit_margin}
          onChange={(v) => updateItemField(index, "profit_margin", v)}
          formatter={(v) => `${v}%`}
          parser={(v) => (v ? v.replace(/%/g, "") : "") as any}
          className="w-20"
        />
      ),
    },
    {
      title: "Wholesale",
      key: "wholesale_price",
      width: 96,
      render: (_: any, rec: EditablePurchaseItem, index: number) => (
        <InputNumber
          size="small"
          min={0}
          value={rec.wholesale_price}
          onChange={(v) => updateItemField(index, "wholesale_price", v)}
          formatter={(v) => `Rs. ${v}`}
          parser={(v) => (v ? v.replace(/Rs\.\s?|(,*)/g, "") : "") as any}
          className="w-24"
        />
      ),
    },
    {
      title: "Disc (%)",
      key: "discount",
      width: 80,
      render: (_: any, rec: EditablePurchaseItem, index: number) => (
        <InputNumber
          size="small"
          min={0}
          max={100}
          value={rec.discount_percent}
          onChange={(v) => updateItemField(index, "discount_percent", v)}
          formatter={(v) => `${v}%`}
          parser={(v) => (v ? v.replace(/%/g, "") : "") as any}
          className="w-16"
        />
      ),
    },
    {
      title: "Subtotal",
      key: "subtotal",
      width: 96,
      render: (_: any, rec: EditablePurchaseItem) => (
        <span className="font-semibold text-gray-700 text-xs">
          Rs. {(rec.total || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: "",
      key: "actions",
      width: 40,
      align: "center" as const,
      render: (_: any, __: any, index: number) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeItem(index)}
        />
      ),
    },
  ];

  return (
    <Drawer
      title={drawerTitle(
        editingPurchase
          ? editingPurchase.status === PurchaseStatus.COMPLETED
            ? `Purchase Record Details (Invoice: ${editingPurchase.invoice_no || "N/A"})`
            : editingPurchase.status === PurchaseStatus.RETURNED
              ? `Returned Purchase Details (Invoice: ${editingPurchase.invoice_no || "N/A"})`
              : editingPurchase.status === PurchaseStatus.PENDING
                ? `Edit Pending Purchase Order (Invoice: ${editingPurchase.invoice_no || "N/A"})`
                : `Edit Purchase Draft (Invoice: ${editingPurchase.invoice_no || "Draft"})`
          : "Register New Product Purchase",
      )}
      extra={
        <div className="flex items-center gap-2">
          {getBulkPrintItems().length > 0 && (
            <Button
              icon={<PrinterOutlined />}
              onClick={() => setBulkPrintOpen(true)}
            >
              Print Barcodes
            </Button>
          )}
          {(!editingPurchase ||
            editingPurchase.status === PurchaseStatus.DRAFT ||
            editingPurchase.status === PurchaseStatus.PENDING) && (
            <>
              <Button
                type="primary"
                size="middle"
                loading={scanning}
                onClick={triggerScanClick}
              >
                Scan Invoice
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleScanInvoice}
                accept="image/jpeg,image/png,image/webp,application/pdf"
                style={{ display: "none" }}
              />
            </>
          )}
        </div>
      }
      open={open}
      onClose={closeForm}
      width={1050}
      footer={drawerFooter({
        left: (
          <span>
            Selected Items:{" "}
            <span className="text-indigo-600 font-semibold">
              {editingPurchase &&
              (editingPurchase.status === PurchaseStatus.COMPLETED ||
                editingPurchase.status === PurchaseStatus.RETURNED) &&
              fullPurchase
                ? fullPurchase.items?.length || 0
                : purchaseItems.length}
            </span>
          </span>
        ),
        actions: [
          { label: "Close", onClick: closeForm },
          ...(!editingPurchase ||
          editingPurchase.status === PurchaseStatus.DRAFT ||
          editingPurchase.status === PurchaseStatus.PENDING
            ? [
                {
                  label: "Save Draft",
                  loading:
                    createPurchaseMutation.isPending ||
                    updatePurchaseMutation.isPending,
                  onClick: () => handleSubmit(PurchaseStatus.DRAFT),
                },
                {
                  label: "Set Pending",
                  loading:
                    createPurchaseMutation.isPending ||
                    updatePurchaseMutation.isPending,
                  onClick: () => handleSubmit(PurchaseStatus.PENDING),
                },
                {
                  label: "Complete Purchase",
                  type: "primary" as const,
                  loading:
                    createPurchaseMutation.isPending ||
                    updatePurchaseMutation.isPending,
                  onClick: handleConfirmComplete,
                },
              ]
            : []),
        ],
      })}
    >
      <Spin
        spinning={scanning || loadingDetails}
        size="large"
        tip={
          scanning
            ? "AI is analyzing invoice document and auto-filling form..."
            : "Loading details..."
        }
      >
        {editingPurchase &&
        (editingPurchase.status === PurchaseStatus.COMPLETED ||
          editingPurchase.status === PurchaseStatus.RETURNED) &&
        fullPurchase ? (
          <PurchaseView
            purchase={fullPurchase}
            onPaymentChange={handlePaymentChange}
          />
        ) : (
          <PurchaseForm
            form={form}
            suppliers={suppliers}
            setSupplierModalOpen={setSupplierModalOpen}
            productSearchText={productSearchText}
            setProductSearchText={setProductSearchText}
            handleProductSelect={handleProductSelect}
            allProducts={allProducts}
            purchaseItems={purchaseItems}
            formTableColumns={formTableColumns}
            globalDiscount={globalDiscount}
            setGlobalDiscount={setGlobalDiscount}
            grossTotal={grossTotal}
            paidAmount={paidAmount}
            setPaidAmount={setPaidAmount}
            showDueDate={showDueDate}
            grandTotal={grandTotal}
            dueAmount={dueAmount}
          />
        )}
      </Spin>

      <SupplierModal
        open={supplierModalOpen}
        onClose={() => {
          setSupplierModalOpen(false);
          refetchSuppliers();
        }}
      />

      <BulkBarcodePrintModal
        open={bulkPrintOpen}
        onClose={() => setBulkPrintOpen(false)}
        items={getBulkPrintItems()}
      />
    </Drawer>
  );
};
